import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa6';
import Sidebar from '../../../../shared/components/Sidebar';
import FormattedText from '../../../../shared/components/FormattedText';
import { useAppContext } from '../../../../shared/context/AppContext';
import { getUserId } from '../../../../shared/domain/services/userService';
import * as promptLibraryService from '../../data/services/promptLibraryService';
import { detectPromptFormat } from '../../domain/services/promptFormatDetector';
import PromptHeader from '../components/PromptHeader';
import PromptContent from '../components/PromptContent';
import FreeTextContent from '../components/FreeTextContent';
import PromptMetadata from '../components/PromptMetadata';
import PromptVersionTabs from '../components/PromptVersionTabs';
import './PromptDetailsScreen.css';
import '../components/PromptDetails.css';

const PromptDetailsScreen = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, organization } = useAppContext();
  const [prompt, setPrompt] = useState(null);
  const [duplicate, setDuplicate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [activeVersion, setActiveVersion] = useState('original'); // 'original' or 'duplicate'
  const [selectedContextVariableIds, setSelectedContextVariableIds] = useState([]);

  const handleBackClick = () => {
    navigate('/home');
  };

  useEffect(() => {
    loadPromptDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, user, organization]);

  const loadPromptDetails = async () => {
    try {
      setLoading(true);
      // Pass userId and organizationId for optimized backend queries
      const userId = user ? getUserId(user) : null;
      const organizationId = organization ? (organization.id || organization.orgId) : null;
      
      if (userId) {
        // Use getPromptWithDuplicate to get both prompt and duplicate
        const { prompt: promptData, duplicate: duplicateData, hasDuplicate } = await promptLibraryService.getPromptWithDuplicate(id, userId, organizationId);
        setPrompt(promptData);
        setDuplicate(duplicateData);
        
        // Default to duplicate view if it exists
        if (hasDuplicate && duplicateData) {
          setActiveVersion('duplicate');
        } else {
          setActiveVersion('original');
        }
      } else {
        // Fallback to regular getPromptById if no user
        const promptData = await promptLibraryService.getPromptById(id, null, organizationId);
        setPrompt(promptData);
        setDuplicate(null);
        setActiveVersion('original');
      }
      
      setError(null);
    } catch (err) {
      console.error('Error loading prompt details:', err);
      setError('Prompt not found');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = (updatedPrompt) => {
    if (activeVersion === 'duplicate') {
      setDuplicate(updatedPrompt);
    } else {
      setPrompt(updatedPrompt);
    }
  };

  const handleEditModeChange = (editMode) => {
    setIsEditMode(editMode);
  };

  const handleVersionChange = (version) => {
    setActiveVersion(version);
    setIsEditMode(false); // Exit edit mode when switching versions
  };

  const handleDuplicate = async (originalPrompt) => {
    try {
      const userId = getUserId(user);
      if (!userId) {
        alert('You must be logged in to duplicate a prompt');
        return;
      }
      
      // Create duplicate
      const duplicateData = await promptLibraryService.duplicatePrompt(originalPrompt.id, userId);
      setDuplicate(duplicateData);
      setActiveVersion('duplicate');
      setIsEditMode(true); // Enter edit mode immediately after duplicating
    } catch (err) {
      console.error('Error duplicating prompt:', err);
      alert('Failed to duplicate prompt. Please try again.');
    }
  };

  // Get the active prompt based on version
  const getActivePrompt = () => {
    return activeVersion === 'duplicate' && duplicate ? duplicate : prompt;
  };

  // Detect prompt format
  const getPromptFormat = () => {
    const activePrompt = getActivePrompt();
    if (!activePrompt) return 'STRUCTURED';
    return detectPromptFormat(activePrompt);
  };

  // Render appropriate content component based on format
  const renderPromptContent = () => {
    const activePrompt = getActivePrompt();
    const format = getPromptFormat();

    if (format === 'FREE_TEXT') {
      return (
        <FreeTextContent
          promptText={activePrompt.promptText}
          promptId={activePrompt.id}
          promptTitle={activePrompt.title}
        />
      );
    } else {
      // STRUCTURED format (legacy format, still supported)
      return (
        <PromptContent
          promptText={activePrompt.promptText}
          promptId={activePrompt.id}
          promptTitle={activePrompt.title}
          isGlobal={activePrompt.isGlobal || false}
          selectedContextVariableIds={selectedContextVariableIds}
          onContextVariableSelectionChange={setSelectedContextVariableIds}
        />
      );
    }
  };

  if (loading) {
    return (
      <div className="prompt-details-screen">
        <Sidebar>
          <div className="details-sidebar-content">
            <button
              className="back-button"
              onClick={handleBackClick}
              aria-label="Back to Prompt Library"
            >
              <FaArrowLeft className="back-icon" />
            </button>
          </div>
        </Sidebar>
        <div className="details-main">
          <div className="prompt-details-card">
            <div className="prompt-details-gradient-header"></div>
            <div className="prompt-details-card-content">
          <div className="loading">Loading prompt...</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="prompt-details-screen">
        <Sidebar>
          <div className="details-sidebar-content">
            <button
              className="back-button"
              onClick={handleBackClick}
              aria-label="Back to Prompt Library"
            >
              <FaArrowLeft className="back-icon" />
            </button>
          </div>
        </Sidebar>
        <div className="details-main">
          <div className="prompt-details-card">
            <div className="prompt-details-gradient-header"></div>
            <div className="prompt-details-card-content">
          <div className="error">{error}</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="prompt-details-screen">
      <Sidebar>
        <div className="details-sidebar-content">
          <button
            className="back-button"
            onClick={handleBackClick}
            aria-label="Back to Prompt Library"
          >
            <FaArrowLeft className="back-icon" />
          </button>
        </div>
      </Sidebar>
      <div className="details-main">
        <div className="prompt-details-card">
          <div className="prompt-details-gradient-header"></div>
          <div className="prompt-details-card-content">
            <PromptHeader 
              prompt={getActivePrompt()} 
              duplicate={duplicate}
              activeVersion={activeVersion}
              onUpdate={handleUpdate} 
              onEditModeChange={handleEditModeChange}
              onDuplicate={handleDuplicate}
              shouldStartInEditMode={isEditMode}
            />

            {duplicate && !isEditMode && (
              <PromptVersionTabs
                activeVersion={activeVersion}
                hasDuplicate={!!duplicate}
                onVersionChange={handleVersionChange}
              />
            )}

            {!isEditMode && (
              <>
                <div className="prompt-description-section">
                  <h2 className="section-subtitle">Description</h2>
                  <p className="prompt-description-text">{getActivePrompt().description || 'No description provided'}</p>
                </div>

                {getActivePrompt().backgroundTheory && (
                  <>
                    <div className="divider-line"></div>
                    <div className="background-theory-section">
                      <h2 className="section-subtitle">Background Theory</h2>
                      <FormattedText text={getActivePrompt().backgroundTheory} className="background-theory-text" />
                    </div>
                  </>
                )}

                {renderPromptContent()}

                <PromptMetadata prompt={getActivePrompt()} />
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PromptDetailsScreen;

